import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Library } from 'lucide-react';
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { useUser } from "@/services/auth-service";

const BookCover = ({ content, onClick = () => {} }) => (
    <div
        onClick={onClick}
        className={cn(
            "h-full w-full relative",
            "rounded-3xl overflow-hidden",
            "bg-[#f8f5e6] dark:bg-gray-900"
        )}
    >
        <img
            src={content.coverImageUrl || '/placeholder-cover.jpg'}
            alt="Book Cover"
            className="object-cover w-full h-full opacity-80"
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center p-8 bg-black/50 rounded-3xl">
            <h1 className="mb-4 font-serif text-4xl text-center text-white">{content.title}</h1>
        </div>
    </div>
);

const BookPage = ({ content, pageNumber, imageUrl, imagePosition, isLeft }) => {
    if (imageUrl && imagePosition !== 'overlay') {
        return (
            <div className={cn(
                "h-full w-full shadow-lg",
                isLeft ? "rounded-l-3xl" : "rounded-r-3xl",
                "relative overflow-hidden"
            )}>
                <img
                    src={imageUrl}
                    alt={`Illustration for page ${pageNumber}`}
                    className="object-cover w-full h-full"
                    style={{
                        maxHeight: '100%',
                        maxWidth: '100%',
                        margin: 'auto'
                    }}
                />
            </div>
        );
    }

    const pageStyle = cn(
        "h-full w-full shadow-lg p-8",
        "bg-[#f8f5e6] text-slate-800 dark:bg-[#d1cebe]",
        "bg-[linear-gradient(45deg,_transparent_48%,_#00000008_49%,_#00000008_51%,_transparent_52%)]",
        "bg-[length:6px_6px]",
        "relative",
        isLeft ? "rounded-l-3xl" : "rounded-r-3xl",
        "before:absolute before:inset-0",
        "before:bg-[linear-gradient(90deg,_rgba(255,255,255,0.07)_0%,_transparent_50%,_rgba(0,0,0,0.05)_100%)]",
        "dark:before:bg-[linear-gradient(90deg,_rgba(255,255,255,0.05)_0%,_transparent_50%,_rgba(0,0,0,0.15)_100%)]",
        isLeft ? "before:rounded-l-3xl" : "before:rounded-r-3xl"
    );

    if (imagePosition === 'overlay') {
        return (
            <div className={pageStyle}>
                <div className="relative h-full">
                    <img
                        src={imageUrl}
                        alt={`Illustration for page ${pageNumber}`}
                        className="object-cover w-full h-full rounded-lg"
                    />
                    <div className="absolute inset-0 flex items-center justify-center p-8 rounded-lg bg-black/40">
                        <p className="font-serif text-xl leading-relaxed text-white">
                            {content}
                        </p>
                    </div>
                </div>
                {pageNumber && (
                    <div className={cn(
                        "absolute bottom-4",
                        isLeft ? "left-4" : "right-4",
                        "text-slate-800 dark:text-slate-800 font-serif"
                    )}>
                        {pageNumber}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={pageStyle}>
            <div className="flex items-center justify-center h-full">
                <p className="text-xl font-serif leading-relaxed max-h-[90%] overflow-hidden">
                    {content}
                </p>
            </div>
            {pageNumber && (
                <div className={cn(
                    "absolute bottom-4",
                    isLeft ? "left-4" : "right-4",
                    "text-slate-800 dark:text-slate-800 font-serif"
                )}>
                    {pageNumber}
                </div>
            )}
        </div>
    );
};

const BookBackCover = ({ user, onClick }) => (
    <div
        onClick={onClick}
        className="h-full w-full relative bg-gradient-to-br from-blue-900 to-blue-950 rounded-3xl overflow-hidden before:absolute before:inset-0 before:bg-[repeating-linear-gradient(45deg,transparent,transparent_2px,rgba(255,255,255,0.1)_2px,rgba(255,255,255,0.1)_4px)] before:rounded-3xl"
    >
        <div className="absolute inset-0 flex flex-col items-center justify-center p-8 font-serif text-white">
            <h2 className="mb-8 text-2xl font-bold">The End</h2>
            <p className="mb-4">Story by: {user?.email || 'Anonymous'}</p>
            <p className="mb-4">Created on: {new Date().toLocaleDateString()}</p>
            <p>© {new Date().getFullYear()} WhimsyQuill</p>
        </div>
    </div>
);

const processPages = (rawPages, isMobile) => {
    const processedPages = [];
    let currentPageNumber = 1;

    rawPages.forEach(page => {
        // Add text page
        processedPages.push({
            type: 'text',
            content: page.content,
            pageNumber: currentPageNumber++,
            imageUrl: page.imagePosition === 'overlay' ? page.imageUrl : null,
            imagePosition: page.imagePosition
        });

        // Add separate image page if needed
        if (page.imageUrl && page.imagePosition !== 'overlay' && page.imagePosition !== 'none') {
            if (isMobile || page.imagePosition === 'right') {
                processedPages.push({
                    type: 'image',
                    imageUrl: page.imageUrl,
                    pageNumber: currentPageNumber++
                });
            } else {
                processedPages.splice(processedPages.length - 1, 0, {
                    type: 'image',
                    imageUrl: page.imageUrl,
                    pageNumber: currentPageNumber++
                });
            }
        }
    });

    // Ensure even number of pages for desktop view
    if (!isMobile && processedPages.length % 2 !== 0) {
        processedPages.push({ type: 'blank', pageNumber: currentPageNumber });
    }

    return processedPages;
};

const BookReader = ({ story }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const [processedPages, setProcessedPages] = useState([]);
    const user = useUser();
    const navigate = useNavigate();

    useEffect(() => {
        const checkMobile = () => {
            const isMobileView = window.innerWidth < 768;
            setIsMobile(isMobileView);
            setProcessedPages(processPages(story.pages, isMobileView));
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, [story.pages]);

    const handlePageClick = (e) => {
        // Prevent click if interacting with navigation buttons
        if (e.target.closest('button')) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const isLeftSide = x < rect.width / 2;

        if (isLeftSide) {
            handlePageTurn('prev');
        } else {
            handlePageTurn('next');
        }
    };

    const isLastPage = currentPage === processedPages.length;

    const handlePageTurn = (direction) => {
        if (!isOpen) {
            setIsOpen(true);
            return;
        }

        if (direction === 'prev') {
            if (currentPage === 0) {
                setIsOpen(false);
            } else {
                setCurrentPage(prev => prev - (isMobile ? 1 : 2));
            }
        } else if (direction === 'next' && !isLastPage) {
            setCurrentPage(prev => prev + (isMobile ? 1 : 2));
        }
    };

    const renderPage = (page, isLeft = true) => {
        if (!page) return null;

        return (
            <BookPage
                content={page.content}
                pageNumber={page.pageNumber}
                imageUrl={page.imageUrl}
                imagePosition={page.imagePosition}
                isLeft={isLeft}
            />
        );
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center overflow-hidden">
            <div className="relative w-full h-full max-w-5xl 2xl:max-w-[1600px] lg:max-h-[85vh] lg:mt-[7vh]">
                {!isOpen ? (
                    <BookCover
                        content={story}
                        onClick={() => handlePageTurn('next')}
                    />
                ) : isLastPage ? (
                    <BookBackCover
                        user={user}
                        onClick={() => handlePageTurn('prev')}
                    />
                ) : (
                    <div
                        className={cn(
                            "w-full h-full cursor-pointer",
                            isMobile ? "" : "flex"
                        )}
                        onClick={handlePageClick}
                    >
                        {isMobile ? (
                            renderPage(processedPages[currentPage], true)
                        ) : (
                            <>
                                <div className="w-1/2 h-full">
                                    {renderPage(processedPages[currentPage], true)}
                                </div>
                                <div className="w-1/2 h-full">
                                    {renderPage(processedPages[currentPage + 1], false)}
                                </div>
                            </>
                        )}
                    </div>
                )}

                <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between p-4 bg-white/80 dark:bg-gray-900/80 backdrop-blur">
                    <button
                        onClick={() => handlePageTurn('prev')}
                        className={cn(
                            "p-4 rounded-full",
                            !isOpen && "opacity-50"
                        )}
                    >
                        <ChevronLeft className="text-black dark:text-white" />
                    </button>
                    <button
                        onClick={() => navigate('/library')}
                        className="p-4 rounded-full"
                    >
                        <Library className="text-black dark:text-white" />
                    </button>
                    <button
                        onClick={() => handlePageTurn('next')}
                        disabled={isLastPage}
                        className={cn(
                            "p-4 rounded-full",
                            isLastPage && "opacity-50"
                        )}
                    >
                        <ChevronRight className="text-black dark:text-white" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BookReader;
